import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { getWarehouseSelectedLocation } from '../../navigation/components/WarehouseLocationToggle';
import {
  useUpdateWarehouseNotificationConfig,
  useWarehouseNotificationConfig,
} from '../hooks/useWarehouseNotificationConfig';
import {
  addGroupMembershipVariables,
  removeGroupMembershipVariables,
  useAddGroupMembership,
  useAssignableUsers,
  useRemoveGroupMembership,
  useWarehouseDepartmentGroups,
} from '../hooks/useWarehouseDepartmentGroups';
import {
  AssignableUser,
  WarehouseDepartmentGroup,
  WarehouseNotificationConfig,
  warehouseDepartmentGroupsUrl,
  warehouseNotificationConfigUrl,
} from '../model';

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const assignableUserLabel = (user: AssignableUser): string => `${user.full_name} (${user.email})`;

const departmentGroupDisplayName = (groupId: string): string =>
  groupId === 'ALL_DEPARTMENTS' ? 'All Departments' : groupId;

const filterAssignableUsers = (options: AssignableUser[], inputValue: string): AssignableUser[] => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((user) => assignableUserLabel(user).toLowerCase().includes(query));
};

const sectionTitleSx = {
  color: 'primary.main',
  fontWeight: 600,
  fontSize: '14px',
} as const;

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'visible',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    borderColor: 'primary.light',
    bgcolor: 'grey.50',
  },
} as const;

const WarehouseNotificationConfigPage: React.FC = () => {
  const [location] = useState(() => getWarehouseSelectedLocation());
  const configUrl = warehouseNotificationConfigUrl(location);
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const { data, isLoading, isError } = useWarehouseNotificationConfig(location);
  const { mutateAsync, isPending } = useUpdateWarehouseNotificationConfig();
  const { data: groupsResponse, isLoading: groupsLoading } = useWarehouseDepartmentGroups(location);
  const { data: usersResponse } = useAssignableUsers();
  const addMembership = useAddGroupMembership();
  const removeMembership = useRemoveGroupMembership();

  const [config, setConfig] = useState<WarehouseNotificationConfig | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [pickerKeys, setPickerKeys] = useState<Record<string, number>>({});

  useEffect(() => {
    const result = data?.data;
    if (result) setConfig(result);
  }, [data?.data]);

  const handleCopyVariable = async (variable: string) => {
    try {
      await navigator.clipboard.writeText(variable);
      showSuccessSnackBar?.(`Copied ${variable}`);
    } catch {
      showErrorSnackBar?.('Failed to copy to clipboard.');
    }
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      const response = await mutateAsync({
        url: configUrl,
        data: { config: { subject: config.subject, body: config.body } },
      });
      const saved = response.data ?? config;
      setConfig(saved);
      showSuccessSnackBar?.('Configuration saved.');
    } catch {
      showErrorSnackBar?.('Failed to save configuration.');
    }
  };

  const bumpPicker = (groupId: string) => {
    setPickerKeys((prev) => ({ ...prev, [groupId]: (prev[groupId] ?? 0) + 1 }));
  };

  const groups: WarehouseDepartmentGroup[] = groupsResponse?.data ?? [];
  const assignableUsers: AssignableUser[] = useMemo(() => usersResponse?.data ?? [], [usersResponse]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [warehouseDepartmentGroupsUrl, { location }] });
  };

  const handleAdd = async (groupId: string, user: AssignableUser | null) => {
    if (!user) return;
    setBusyKey(`${groupId}:${user.id}`);
    try {
      await addMembership.mutateAsync(addGroupMembershipVariables(user.id, groupId, location));
      await refresh();
      bumpPicker(groupId);
      showSuccessSnackBar?.(`Added ${user.full_name} to ${departmentGroupDisplayName(groupId)}`);
    } catch {
      showErrorSnackBar?.('Could not add user to department');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemove = async (membershipId: number, groupId: string, fullName: string) => {
    setBusyKey(`${groupId}:${membershipId}`);
    try {
      await removeMembership.mutateAsync(removeGroupMembershipVariables(membershipId));
      await refresh();
      showSuccessSnackBar?.(`Removed ${fullName} from ${departmentGroupDisplayName(groupId)}`);
    } catch {
      showErrorSnackBar?.('Could not remove user from department');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Warehouse — Notifications" titleIcon={<NotificationsIcon />} />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        {isLoading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        {isError && <ErrorMessage message="Failed to load configuration." />}
        {!isLoading && !isError && config && (
          <LoadingWrapper isLoading={isPending} message="Saving...">
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Email Template
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box>
                <TextField
                  label="Email Subject"
                  fullWidth
                  value={config.subject}
                  onChange={(e) => setConfig({ ...config, subject: e.target.value })}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Template Variables — click to copy, then paste into the subject or body
                  </Typography>
                  {(config.template_variables ?? []).map((variable) => (
                    <Chip
                      key={variable}
                      icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      label={variable}
                      onClick={() => handleCopyVariable(variable)}
                      clickable
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Email Body
                </Typography>
                {/* height sets the container; marginBottom prevents the Quill toolbar from overlapping elements below */}
                <ReactQuill
                  placeholder="Enter email body here..."
                  theme="snow"
                  value={config.body}
                  onChange={(value) => setConfig({ ...config, body: value })}
                  modules={QUILL_MODULES}
                  style={{ height: 200, marginBottom: 48 }}
                />

                <Box>
                  <Button variant="contained" color="primary" onClick={handleSave}>
                    Save Configuration
                  </Button>
                </Box>
              </Box>
            </Paper>
          </LoadingWrapper>
        )}
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" sx={sectionTitleSx} gutterBottom>
          Departments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Expand a department to add or remove the users who belong to it.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: 'primary.light', opacity: 0.5 }} />

        {groupsLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
            {groups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No departments for this location.
              </Typography>
            )}
            <Grid container columnSpacing={1.5} rowSpacing={0.5}>
              {groups.map((group) => {
                const memberIds = new Set(group.users.map((u) => u.id));
                const options = assignableUsers.filter((u) => !memberIds.has(u.id));
                return (
                  <Grid item xs={12} md={6} key={group.group_id}>
                    <Accordion disableGutters sx={accordionSx}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                        sx={{
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, mr: 0.5 }}>
                          {departmentGroupDisplayName(group.group_id)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 'auto', mr: 1, whiteSpace: 'nowrap' }}
                        >
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {group.users.length}
                          </Box>{' '}
                          users
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ overflow: 'visible', pt: 0 }}>
                        <List dense disablePadding>
                          {group.users.map((user) => (
                            <ListItem
                              key={user.membership_id}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                bgcolor: 'grey.50',
                                borderLeft: '2px solid',
                                borderLeftColor: 'primary.light',
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  size="small"
                                  disabled={busyKey === `${group.group_id}:${user.membership_id}`}
                                  onClick={() => handleRemove(user.membership_id, group.group_id, user.full_name)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={user.full_name}
                                secondary={user.email}
                                primaryTypographyProps={{ fontWeight: 500 }}
                              />
                            </ListItem>
                          ))}
                        </List>

                        <Autocomplete
                          key={`add-user-${group.group_id}-${pickerKeys[group.group_id] ?? 0}`}
                          options={options}
                          getOptionLabel={assignableUserLabel}
                          isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
                          value={null}
                          blurOnSelect
                          filterOptions={(opts, state) => filterAssignableUsers(opts, state.inputValue)}
                          onChange={(_event, value) => handleAdd(group.group_id, value)}
                          ListboxProps={{ style: { maxHeight: 280 } }}
                          componentsProps={{ popper: { placement: 'bottom-start', style: { zIndex: 1500 } } }}
                          renderInput={(params) => (
                            <TextField {...params} label="Add user" size="small" color="primary" autoComplete="off" />
                          )}
                          sx={{ maxWidth: 420, mt: 1 }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default WarehouseNotificationConfigPage;
